import { RootState } from 'store';
import { CardWithPopoverProps } from 'components/Dashboard/components/CardsPage/types';
import DashboardCard from 'components/Dashboard/components/CardsPage/DashboardCard/DashboardCard';
import { getUserProfileProgress } from 'common/helpers/user';
import { getShippingProgress } from 'common/helpers/shipping';
import { getShippingCardLabel } from 'components/Dashboard/components/CardsPage/helpers';
import { SHIPPING_ROUTE } from 'common/constants/urls';
import { USA } from 'common/constants/countries';

import { useSelector } from 'react-redux';
import { Box, Button, Grid, Popover, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const ShippingDashboardCard: React.FC<CardWithPopoverProps> = ({ forceShowPopover, ...props }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { myUser } = useSelector((state: RootState) => state.user);
  const { transactions, returnShippingStatus, directShippingStatus } = useSelector((state: RootState) => state.shipping);

  const [ isPopoverOpened, setPopoverOpened ] = useState(false);
  const cardRef = React.useRef<HTMLElement | null>(null);

  const profileProgress = getUserProfileProgress(myUser);
  const isProfileCompleted = profileProgress === 100;
  const isSurveyCompleted = myUser.surveyCompletedPercentage === 100;
  const isUserCountryUSA = myUser.mailingAddress?.country === USA;

  let shippingProgress: number;
  if (myUser.testKitReceived) {
    shippingProgress = 100;
  } else if (!isUserCountryUSA) {
    shippingProgress = -1;
  } else {
    shippingProgress = isProfileCompleted && isSurveyCompleted ? getShippingProgress(transactions, returnShippingStatus, directShippingStatus) : -1;
  }

  const shippingButtonLabel = t(getShippingCardLabel(shippingProgress));

  const hidePopover = () => {
    setPopoverOpened(false);
    navigate(location.pathname, { state: {} });
  };

  const showPopover = () => {
    if (shippingProgress === -1) {
      setPopoverOpened(true);
    }
  };

  useEffect(() => {
    if (forceShowPopover) {
      setPopoverOpened(true);
    }
  }, [ setPopoverOpened, forceShowPopover ]);

  return (
    <>
      <Box
        ref={cardRef}
        onClick={showPopover}
        sx={{
          pointerEvents: isPopoverOpened ? 'none' : 'auto',
        }}
        {...props}
      >
        <DashboardCard
          label={t('dashboard.shipping.card_title')}
          buttonLabel={shippingButtonLabel}
          link={SHIPPING_ROUTE}
          progress={shippingProgress}
          disabled={shippingProgress === 100}
        />
      </Box>
      <Popover
        BackdropProps={{
          invisible: false,
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
          },
        }}
        open={isPopoverOpened}
        anchorEl={cardRef.current}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: { borderRadius: '30px' },
          elevation: 5,
        }}
        disableRestoreFocus
      >
        <Grid container padding={3} spacing={4} maxWidth={425}>
          <Grid item xs={12}>
            <Typography variant="textXLBold">
              {t('dashboard.shipping.card_lock_dialog.title')}
            </Typography>
          </Grid>
          {isUserCountryUSA ? (
            <Grid item xs={12}>
              <Typography>
                {t('dashboard.shipping.card_lock_dialog.body')}
              </Typography>
              <ul>
                {!isProfileCompleted && <li><Typography>{t('dashboard.profile.profile')}</Typography></li>}
                {!isSurveyCompleted && <li><Typography>{t('dashboard.survey.survey')}</Typography></li>}
              </ul>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Typography>
                {t('dashboard.shipping.card_lock_dialog.not_available_in_your_country')}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button variant="outlined" fullWidth onClick={hidePopover}>
              {t('dashboard.shipping.card_lock_dialog.button_got_it')}
            </Button>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default ShippingDashboardCard;
